/**
 * Action Log Modal - Shows the detailed action extraction and execution process
 */

import React from 'react';
import { X } from 'lucide-react';
import './ActionLogModal.css';

interface ActionLogModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: {
    transcript?: string;
    extractedActions?: Array<{
      actionId: string;
      params: any;
      confidence: number;
    }>;
    executed?: Array<{
      actionId: string;
      params: any;
      status?: string;
      message?: string;
    }>;
    alreadyExists?: Array<{
      actionId: string;
      params: any;
      status?: string;
      message?: string;
    }>;
    failed?: Array<{
      actionId: string;
      reason: string;
    }>;
  };
}

const ActionLogModal: React.FC<ActionLogModalProps> = ({ isOpen, onClose, data }) => {
  if (!isOpen) return null;

  return (
    <div className="action-log-overlay" onClick={onClose}>
      <div className="action-log-modal" onClick={(e) => e.stopPropagation()}>
        <div className="action-log-header">
          <h2>Action Extraction Log</h2>
          <button className="action-log-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="action-log-content">
          {/* Step 1: Transcript */}
          <div className="log-section">
            <div className="log-section-header">
              <span className="log-icon">📋</span>
              <h3>Step 1: Transcript Extracted</h3>
            </div>
            <div className="log-section-body">
              {data.transcript ? (
                <pre className="transcript-text">{data.transcript}</pre>
              ) : (
                <p className="log-empty">No transcript available</p>
              )}
            </div>
          </div>

          {/* Step 2: Sent to GPT */}
          <div className="log-section">
            <div className="log-section-header">
              <span className="log-icon">🤖</span>
              <h3>Step 2: Sent to GPT-4o</h3>
            </div>
            <div className="log-section-body">
              <p className="log-info">
                GPT-4o analyzed the conversation and found{' '}
                <strong>{data.extractedActions?.length || 0}</strong> action(s) to take
              </p>
            </div>
          </div>

          {/* Step 3: GPT's Interpretation */}
          {data.extractedActions && data.extractedActions.length > 0 && (
            <div className="log-section">
              <div className="log-section-header">
                <span className="log-icon">🎯</span>
                <h3>Step 3: GPT's Interpretation</h3>
              </div>
              <div className="log-section-body">
                {data.extractedActions.map((action, index) => (
                  <div key={index} className="action-item">
                    <div className="action-number">{index + 1}</div>
                    <div className="action-details">
                      <div className="action-id">{action.actionId}</div>
                      <div className="action-params">
                        <strong>Params:</strong> {JSON.stringify(action.params, null, 2)}
                      </div>
                      <div className="action-confidence">
                        <strong>Confidence:</strong> {(action.confidence * 100).toFixed(0)}%
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step 4: Actions Executed */}
          {data.executed && data.executed.length > 0 && (
            <div className="log-section success">
              <div className="log-section-header">
                <span className="log-icon">✅</span>
                <h3>Step 4: Actions Executed</h3>
              </div>
              <div className="log-section-body">
                {data.executed.map((action, index) => (
                  <div key={index} className="action-result success">
                    <span className="result-icon">✓</span>
                    <span className="result-text">
                      {action.actionId}: {JSON.stringify(action.params)}
                      {action.message && (
                        <div className="action-message">{action.message}</div>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step 4.5: Actions Already Exist */}
          {data.alreadyExists && data.alreadyExists.length > 0 && (
            <div className="log-section warning">
              <div className="log-section-header">
                <span className="log-icon">ℹ️</span>
                <h3>Step 4.5: Already Exists</h3>
              </div>
              <div className="log-section-body">
                {data.alreadyExists.map((action, index) => (
                  <div key={index} className="action-result warning">
                    <span className="result-icon">ℹ️</span>
                    <span className="result-text">
                      {action.actionId}: {JSON.stringify(action.params)}
                      {action.message && (
                        <div className="action-message">{action.message}</div>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step 5: Actions Failed */}
          {data.failed && data.failed.length > 0 && (
            <div className="log-section error">
              <div className="log-section-header">
                <span className="log-icon">❌</span>
                <h3>Actions Failed</h3>
              </div>
              <div className="log-section-body">
                {data.failed.map((action, index) => (
                  <div key={index} className="action-result error">
                    <span className="result-icon">✗</span>
                    <span className="result-text">
                      {action.actionId}: {action.reason}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* No actions case */}
          {(!data.extractedActions || data.extractedActions.length === 0) && (
            <div className="log-section">
              <div className="log-section-header">
                <span className="log-icon">ℹ️</span>
                <h3>Result</h3>
              </div>
              <div className="log-section-body">
                <p className="log-info">No actions needed from this conversation.</p>
              </div>
            </div>
          )}
        </div>

        <div className="action-log-footer">
          <button className="action-log-button" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ActionLogModal;
